import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Award, Heart, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { therapistInfo, heroImages, businessInfo } from '../data/mock';
import SEO from '../components/SEO';

const About = () => {
  return (
    <>
      <SEO 
        title="About Rhonda Zook, RN, MAMFT - Marriage & Family Therapist"
        description="Meet Rhonda Zook, a compassionate Marriage and Family Therapist in Lebanon, PA with 28 years of nursing experience and 5 years of therapy experience. Specializing in individual, couples, and family therapy using evidence-based approaches."
        keywords="Rhonda Zook therapist, MAMFT Lebanon PA, registered nurse therapist, marriage family therapist Lebanon, couples counselor Pennsylvania, systemic therapy"
        url="/about"
        image="https://customer-assets.emergentagent.com/job_mindful-counsel-3/artifacts/lf7ferku_Rhonda-104.jpg"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-50 via-white to-slate-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal-100/50 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <img 
              src={businessInfo.logo} 
              alt={businessInfo.name}
              className="h-32 w-auto mx-auto mb-6"
            />
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">About Me</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mt-2">
              Meet Your Therapist
            </h1>
          </div>
        </div>
      </section>

      {/* Main About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Photo & Quick Info */}
            <div className="space-y-8">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-slate-200">
                  <img 
                    src={therapistInfo.headshot} 
                    alt={`${therapistInfo.name}, ${therapistInfo.credentials}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-teal-500/20 rounded-full blur-2xl" />
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-slate-200/50 rounded-full blur-3xl" />
              </div>

              {/* Name Card */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-1">{therapistInfo.name}</h2>
                  <p className="text-teal-100">{therapistInfo.credentials}</p>
                </CardContent>
              </Card>
            </div>

            {/* Bio Content */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-slate-800 mb-4">My Story</h3>
                <div className="prose prose-slate max-w-none">
                  {therapistInfo.bio.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-slate-600 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              {/* Education */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800">Education</h3>
                </div>
                <ul className="space-y-2">
                  {therapistInfo.education.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Certifications */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Award className="h-5 w-5 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800">Certifications & Training</h3>
                </div>
                <ul className="space-y-2">
                  {therapistInfo.certifications.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Therapeutic Approach Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <Leaf className="h-6 w-6 text-teal-600" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              My Therapeutic Approach
            </h2>
          </div>

          <Card className="border-0 shadow-xl max-w-4xl mx-auto">
            <CardContent className="p-8 sm:p-12">
              <div className="prose prose-slate max-w-none">
                {therapistInfo.approach.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-slate-600 leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 text-center mb-12">
            Core Values
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Compassion",
                description: "Every person deserves to be met with kindness and understanding. I approach each session with genuine care for your well-being.",
                icon: Heart
              },
              {
                title: "Growth",
                description: "I believe in your capacity for change and growth. Together, we'll nurture the seeds of transformation within you.",
                icon: Leaf
              },
              {
                title: "Authenticity",
                description: "Creating a genuine, trustworthy therapeutic relationship is the foundation of meaningful healing work.",
                icon: Award
              }
            ].map((value, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="h-8 w-8 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            I would be honored to support you on your journey toward healing and growth. Reach out to schedule a consultation and take the first step.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg">
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
